// ════════════════════════════════════════════════════════════════════
//  וידג'ט נגישות (תקנה 35 / ת"י 5568) — כלי העדפות-משתמש.
//
//  שתי החלטות עיצוב שנובעות מבאג אמיתי שדווח:
//  1. אין שכבת רקע כהה מאחורי הפאנל. קודם היה bg-black/20 שהעמעם את כל
//     העמוד — זו הייתה ה"הילה השחורה", והיא גם הסתירה את השינוי עצמו, כך
//     שהמשתמש היה חייב לסגור את התפריט כדי לראות מה עשה.
//  2. הפאנל מעוגן לצד המסך ולא במרכזו, כך שהתוכן נשאר גלוי וכל שינוי
//     נראה מיד תוך כדי לחיצה.
//
//  משנה רק קלאסים ומשתני CSS על <html> דרך a11yStore. לא נוגע ב-DOM של
//  התוכן, לא מזריק alt/aria — הקו שמפריד כלי נגישות תקין מ-overlay אסור.
// ════════════════════════════════════════════════════════════════════
import React, { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Accessibility, Contrast, Link2, MousePointer2, Pause, RotateCcw, X, Eye, Heading, ImageOff, Minus, Plus, AlignJustify, Type,
  Space, Baseline, Ruler,
} from "lucide-react";
import { a11yStore, useA11yPrefs } from "@/lib/a11yPrefs";
import { RANGES, clampPref } from "@/lib/a11yRules";

const contrastModes = ["off", "high", "invert", "mono", "bw"];
const contrastLabel = { off: "כבוי", high: "ניגודיות גבוהה", invert: "היפוך צבעים", mono: "גווני אפור", bw: "שחור־לבן" };
const cursorModes = ["off", "light", "dark"];
const cursorLabel = { off: "רגיל", light: "סמן בהיר", dark: "סמן כהה" };

/* כפתור מצב — בינארי (aria-pressed) או מחזורי (הערך בשם הנגיש) */
function Tile({ icon: Icon, label, value, active, onClick, cycle }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={cycle ? undefined : active}
      aria-label={cycle ? `${label}: ${value}` : label}
      className={`flex flex-col items-center justify-center gap-1.5 rounded-xl border p-2 sm:p-3 text-center transition-colors min-h-[80px] sm:min-h-[92px] focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 ${
        active ? "border-primary bg-primary/10 text-primary" : "border-border bg-card text-foreground hover:bg-muted"
      }`}
    >
      <Icon className="w-5 h-5 sm:w-6 sm:h-6" aria-hidden="true" />
      <span className="text-[11px] sm:text-xs font-bold leading-tight">{label}</span>
      {value != null && <span className="text-[11px] text-muted-foreground leading-none">{value}</span>}
    </button>
  );
}

/* שורת +/− עם קריאה מספרית — הרבה יותר ברור ממחזור בין 3 מצבים.
   role="group" עם aria-labelledby, והערך נקרא מ-aria-live בכל שינוי. */
function Stepper({ id, icon: Icon, label, prefKey, value }) {
  const r = RANGES[prefKey];
  const set = (v) => a11yStore.set({ [prefKey]: clampPref(prefKey, v) });
  const display = `${value}${r.unit}`;
  return (
    <div role="group" aria-labelledby={`${id}-label`} className="mb-3">
      <div id={`${id}-label`} className="flex items-center gap-1.5 text-xs font-bold text-foreground mb-1.5">
        <Icon className="w-4 h-4 text-primary" aria-hidden="true" /> {label}
      </div>
      <div className="flex items-stretch gap-1.5">
        <button
          type="button" onClick={() => set(value - r.step)} disabled={value <= r.min}
          aria-label={`הקטנת ${label}`}
          className="w-9 sm:w-12 h-11 rounded-lg border border-border bg-card hover:bg-muted disabled:opacity-40 flex items-center justify-center focus-visible:outline focus-visible:outline-2"
        ><Minus className="w-4 h-4" aria-hidden="true" /></button>
        <output
          aria-live="polite"
          className="flex-1 min-w-0 h-11 rounded-lg border border-border bg-muted/40 text-sm sm:text-base font-bold text-foreground flex items-center justify-center tabular"
        >{display}</output>
        <button
          type="button" onClick={() => set(value + r.step)} disabled={value >= r.max}
          aria-label={`הגדלת ${label}`}
          className="w-9 sm:w-12 h-11 rounded-lg border border-border bg-card hover:bg-muted disabled:opacity-40 flex items-center justify-center focus-visible:outline focus-visible:outline-2"
        ><Plus className="w-4 h-4" aria-hidden="true" /></button>
      </div>
    </div>
  );
}

export default function AccessibilityWidget() {
  const prefs = useA11yPrefs();
  const [open, setOpen] = useState(false);
  const [announce, setAnnounce] = useState("");
  const panelRef = useRef(null);
  const triggerRef = useRef(null);

  // Alt+A — בלתי-תלוי-פריסה (e.code ולא e.key: ב-macOS Alt+A נותן 'å')
  useEffect(() => {
    const onKey = (e) => {
      if (e.altKey && !e.ctrlKey && !e.metaKey && !e.shiftKey && e.code === "KeyA") {
        e.preventDefault(); setOpen((o) => !o);
      }
      if (e.key === "Escape" && open) { setOpen(false); triggerRef.current?.focus(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  useEffect(() => { if (open) panelRef.current?.focus(); }, [open]);

  // לחיצה בכל מקום באתר סוגרת את התפריט. מימוש כמאזין ולא כשכבת רקע —
  // שכבה הייתה מחזירה את העמעום שהסתיר את השינויים (ראו ההערה למעלה),
  // ומאזין גם מאפשר ללחיצה להמשיך אל העמוד כרגיל.
  useEffect(() => {
    if (!open) return;
    const onDown = (e) => {
      if (panelRef.current?.contains(e.target)) return;
      if (triggerRef.current?.contains(e.target)) return;  // הטריגר מטפל בעצמו
      setOpen(false);
    };
    document.addEventListener("pointerdown", onDown, true);
    return () => document.removeEventListener("pointerdown", onDown, true);
  }, [open]);

  // סרגל קריאה — עוקב אחרי הסמן. לא נוגע ב-DOM של התוכן, רק אלמנט צף.
  const guideRef = useRef(null);
  useEffect(() => {
    if (!prefs.readingGuide) return;
    const onMove = (e) => { if (guideRef.current) guideRef.current.style.top = `${e.clientY - 18}px`; };
    window.addEventListener("mousemove", onMove, { passive: true });
    return () => window.removeEventListener("mousemove", onMove);
  }, [prefs.readingGuide]);

  // הכרזה לקוראי מסך. setTimeout ולא rAF — rAF נחנק בטאב רקע וההכרזה אובדת.
  const say = (msg) => { setAnnounce(""); setTimeout(() => setAnnounce(msg), 40); };

  // קוראים מה-store החי ולא מה-snapshot של React, כדי ששתי לחיצות מהירות
  // באותו tick יתקדמו נכון (React לא מרנדר ביניהן).
  const cycleVal = (arr, key, labels, name) => {
    const cur = a11yStore.get()[key];
    const v = arr[(arr.indexOf(cur) + 1) % arr.length];
    a11yStore.set({ [key]: v }); say(`${name}: ${labels[v]}`);
  };
  const toggle = (key, name) => { const v = !a11yStore.get()[key]; a11yStore.set({ [key]: v }); say(`${name}: ${v ? "פעיל" : "כבוי"}`); };
  const reset = () => { a11yStore.reset(); say("כל הגדרות הנגישות אופסו"); };

  const activeCount =
    (prefs.contrast !== "off") + (prefs.textScale > 100) + (prefs.lineScale > 100) +
    (prefs.wordSpace > 0) + (prefs.letterSpace > 0) + (prefs.cursor !== "off") +
    prefs.links + prefs.headings + prefs.readable + prefs.readingGuide +
    prefs.hideImages + prefs.reduceMotion;

  return createPortal(
    <>
      <div role="status" aria-live="polite" aria-atomic="true" className="sr-only">{announce}</div>

      {prefs.readingGuide && <div ref={guideRef} className="a11y-reading-guide" aria-hidden="true" />}

      <button
        ref={triggerRef}
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-label={open ? "סגירת תפריט הנגישות" : "פתיחת תפריט הנגישות"}
        aria-expanded={open}
        aria-controls="a11y-panel"
        aria-keyshortcuts="Alt+A"
        title="נגישות (Alt+A)"
        className="group fixed z-[60] flex items-center justify-center w-10 h-12 sm:w-11 sm:h-14 rounded-e-2xl bg-primary text-white shadow-lg opacity-90 hover:opacity-100 hover:w-12 sm:hover:w-14 focus-visible:opacity-100 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 transition-[width,opacity] duration-200"
        style={{ insetInlineStart: 0, bottom: "calc(var(--bottom-stack, 0px) + 5.5rem)" }}
      >
        <Accessibility className="w-5 h-5 sm:w-6 sm:h-6 a11y-keep" aria-hidden="true" />
        {activeCount > 0 && (
          <span className="absolute -top-1.5 left-0 min-w-[18px] h-[18px] px-1 rounded-full bg-green-700 text-white text-[10px] font-bold flex items-center justify-center" aria-hidden="true">{activeCount}</span>
        )}
      </button>

      {open && (
        /* בלי שכבת עמעום מאחור — התוכן חייב להישאר גלוי כדי שכל שינוי ייראה מיד */
        <div
          id="a11y-panel"
          ref={panelRef}
          role="dialog"
          aria-modal="false"
          aria-label="הגדרות נגישות"
          tabIndex={-1}
          dir="rtl"
          /* מגירה מלאת-גובה מהצד: בדסקטופ יש מקום לפקדים גדולים יותר, ובסלולר
             שום דבר לא נחתך למעלה (קודם הפאנל עוגן ל-bottom וגלש מעל המסך).
             התוכן נשאר גלוי וניתן לשימוש — אין שכבת עמעום. */
          className="fixed z-[61] top-0 bottom-0 w-[45vw] min-w-[168px] max-w-[380px] sm:w-[380px] overflow-y-auto overscroll-contain bg-card shadow-2xl p-3 sm:p-4 focus:outline-none border-border [border-inline-end-width:2px] animate-in slide-in-from-left duration-200"
          style={{ insetInlineStart: 0 }}
        >
          <div className="flex items-center justify-between mb-4 sticky top-0 bg-card pb-2 -mt-1 pt-1 z-10 border-b border-border">
            <h2 className="text-lg font-heading font-extrabold text-foreground flex items-center gap-2">
              <Accessibility className="w-5 h-5 text-primary a11y-keep" aria-hidden="true" /> כלי נגישות
            </h2>
            <button type="button" onClick={() => { setOpen(false); triggerRef.current?.focus(); }} aria-label="סגירת תפריט הנגישות"
              className="p-1.5 rounded-lg hover:bg-muted focus-visible:outline focus-visible:outline-2">
              <X className="w-5 h-5 a11y-keep" aria-hidden="true" />
            </button>
          </div>

          {/* צבע וניגודיות */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            <Tile icon={Contrast} label="ניגודיות" value={contrastLabel[prefs.contrast]} active={prefs.contrast !== "off"} cycle
                  onClick={() => cycleVal(contrastModes, "contrast", contrastLabel, "ניגודיות")} />
            <Tile icon={Link2} label="הדגשת קישורים" active={prefs.links} onClick={() => toggle("links", "הדגשת קישורים")} />
            <Tile icon={Heading} label="הדגשת כותרות" active={prefs.headings} onClick={() => toggle("headings", "הדגשת כותרות")} />
          </div>

          {/* גדלים וריווח — +/− עם קריאה מספרית */}
          <Stepper id="a11y-text"  icon={Type}          label="גודל טקסט"        prefKey="textScale"   value={prefs.textScale} />
          <Stepper id="a11y-line"  icon={AlignJustify}  label="מרווח שורות"      prefKey="lineScale"   value={prefs.lineScale} />
          <Stepper id="a11y-word"  icon={Space}         label="ריווח בין מילים"  prefKey="wordSpace"   value={prefs.wordSpace} />
          <Stepper id="a11y-lett"  icon={Baseline}      label="ריווח בין אותיות" prefKey="letterSpace" value={prefs.letterSpace} />

          {/* עזרי קריאה */}
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mb-4">
            <Tile icon={Eye} label="גופן קריא" active={prefs.readable} onClick={() => toggle("readable", "גופן קריא")} />
            <Tile icon={Ruler} label="סרגל קריאה" active={prefs.readingGuide} onClick={() => toggle("readingGuide", "סרגל קריאה")} />
            <Tile icon={ImageOff} label="הסתרת תמונות" active={prefs.hideImages} onClick={() => toggle("hideImages", "הסתרת תמונות")} />
            <Tile icon={MousePointer2} label="סמן גדול" value={cursorLabel[prefs.cursor]} active={prefs.cursor !== "off"} cycle
                  onClick={() => cycleVal(cursorModes, "cursor", cursorLabel, "סמן גדול")} />
            <Tile icon={Pause} label="עצירת אנימציות" active={prefs.reduceMotion} onClick={() => toggle("reduceMotion", "עצירת אנימציות")} />
            <Tile icon={RotateCcw} label="איפוס הכל" active={false} onClick={reset} />
          </div>

          <p className="text-[10px] text-muted-foreground leading-snug text-center border-t border-border pt-2.5">
            כלי להעדפות אישיות. ההגדרות נשמרות במכשיר שלכם.
            <br />
            <a href="/accessibility/" className="text-primary underline font-medium">להצהרת הנגישות המלאה</a>
          </p>
        </div>
      )}
    </>,
    document.body
  );
}
